import React, {
  memo,
  useMemo,
} from 'react';

import {
  Button,
  Space,
  Typography,
} from 'antd';
import { FaFaceFrown } from 'react-icons/fa6';
import TMIcon from 'tm-icons-library';

import { ArrowRightOutlined } from '@ant-design/icons';

import Loader from '../../../../components/V2/Loader/Loader';
import { useTheme } from '../../../../context/ThemeProvider';
import { useTQConnectConfig } from '../../../../hooks/queries/tqConnectQueries';
import {
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import { dynamicItemTitle } from '../../../../shared/utils';
import { ROUTES } from '../../../../types/routes';
import NoDataFound from '../../../Feed/components/NoDataFound/NoDataFound';

enum TQConnectSetupStatus {
  TQ_SETUP = 'tq-setup',
  TQ_SETUP_COMPLETE = 'tq-setup-complete',
  PROFILE_SETUP = 'profile-setup',
  PROFILE_SETUP_INCOMPLETE = 'profile-setup-incomplete',
  TQ_SETUP_NOT_ENABLED_IN_MANGOES = 'tq-setup-not-enabled-in-mangoes',
  PROFILE_SETUP_COMPLETE = 'profile-setup-complete',
}

type TQConnectSetupStatusType = `${TQConnectSetupStatus}`;

interface TQConnectSetupCardContent {
  title: string;
  subtitle: string;
  buttonText?: string;
  buttonOnClick?: () => void;
}

const TQConnectInfoCard: React.FC = () => {
  const navigate = useAppNavigate();
  const { colors: COLORS } = useTheme();

  const {
    data: tqConnectConfigResult,
    isLoading: isTQConnectConfigLoading,
    error,
  } = useTQConnectConfig();

  const { isDefaultConfig, tqConnectConfig } = tqConnectConfigResult || {};
  const { setupCompleted, enabled, allowedMangoes, profileCompletionQueries } =
    tqConnectConfig || {};
  const label = dynamicItemTitle('tq-title', 'default');
  const tqScoreName = dynamicItemTitle('tq-score', 'default');

  const { tqConnectProfileCompletionResponses } = useAppSelector(
    (state) => state.user,
  );
  const {
    userCommunityAccess: {
      hasCommunityCreatorAccess,
      hasTQConnectAccessForUser,
    },
  } = useAppSelector((state) => state.community);

  const status: TQConnectSetupStatusType = useMemo(() => {
    // check if tq connect is set up and already enabled in at least one mango
    if (
      hasCommunityCreatorAccess &&
      !isDefaultConfig &&
      setupCompleted &&
      enabled &&
      allowedMangoes &&
      allowedMangoes.length > 0
    )
      return TQConnectSetupStatus.TQ_SETUP_COMPLETE;

    // check if user has completed all profile completion queries or some are still pending
    if (
      hasTQConnectAccessForUser &&
      tqConnectProfileCompletionResponses &&
      profileCompletionQueries
    )
      return tqConnectProfileCompletionResponses.length ===
        profileCompletionQueries?.length
        ? TQConnectSetupStatus.PROFILE_SETUP_COMPLETE
        : TQConnectSetupStatus.PROFILE_SETUP_INCOMPLETE;

    return hasCommunityCreatorAccess
      ? TQConnectSetupStatus.TQ_SETUP
      : TQConnectSetupStatus.PROFILE_SETUP;
  }, [
    isDefaultConfig,
    setupCompleted,
    enabled,
    allowedMangoes,
    hasTQConnectAccessForUser,
    tqConnectProfileCompletionResponses,
    profileCompletionQueries,
    hasCommunityCreatorAccess,
  ]);

  const CONTENT: Record<TQConnectSetupStatus, TQConnectSetupCardContent> =
    useMemo(
      () => ({
        [TQConnectSetupStatus.TQ_SETUP]: {
          title: `Introducing ${label}`,
          subtitle:
            'Define how your members earn credibility, not just points.',
        },
        [TQConnectSetupStatus.TQ_SETUP_COMPLETE]: {
          title: `${tqScoreName} is set up!`,
          subtitle: `You’ve successfully created your ${label} system for your members.`,
        },
        [TQConnectSetupStatus.TQ_SETUP_NOT_ENABLED_IN_MANGOES]: {
          title: `${tqScoreName} is set up — But not live yet!`,
          subtitle: `You’ve successfully created your ${label} system, but it hasn’t been enabled for any ${dynamicItemTitle('mangoes')} yet.`,
        },
        [TQConnectSetupStatus.PROFILE_SETUP]: {
          title: `Introducing ${label}`,
          subtitle:
            'Complete your profile to unlock your trust journey and start earning credibility!',
          buttonText: 'Complete Profile',
          buttonOnClick: () => {
            navigate(ROUTES.TQ_CONNECT_PROFILE_COMPLETE_QUESTIONS);
          },
        },
        [TQConnectSetupStatus.PROFILE_SETUP_INCOMPLETE]: {
          title: `${label} - Complete profile`,
          subtitle:
            'Complete your profile to unlock your trust journey and start earning credibility!',
          buttonText: 'Complete Profile',
          buttonOnClick: () => {
            navigate(ROUTES.TQ_CONNECT_PROFILE_COMPLETE_QUESTIONS);
          },
        },
        [TQConnectSetupStatus.PROFILE_SETUP_COMPLETE]: {
          title: `🎉 Profile completed!`,
          subtitle: `🚀 You’re now ready to create or request ${dynamicItemTitle(
            'tq-offerings',
            'lower',
          )}, and unlock meaningful opportunities within the community.`,
        },
      }),
      [label, navigate, tqScoreName],
    );

  if (isTQConnectConfigLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}>
        <Loader type="dots" />
      </div>
    );
  }

  if (error) {
    return (
      <NoDataFound
        iconElement={<FaFaceFrown size={64} color={COLORS.ICON} />}
        title={
          <Typography.Title level={3}>
            Oops! Something went wrong on our end.
          </Typography.Title>
        }
        subtitle="The page may have crashed. Please try reloading the page."
        subtitle2="If the issue persists again and again, please contact support."
        extra={
          <Space align="center" size={12} style={{ marginTop: 8 }}>
            <Button
              className="siteBtn darkBtn"
              onClick={() => window.location.reload()}>
              <TMIcon name="reload" size={16} color="currentColor" />
              Reload Page
            </Button>
          </Space>
        }
      />
    );
  }

  if (
    TQConnectSetupStatus.TQ_SETUP_COMPLETE === status ||
    TQConnectSetupStatus.PROFILE_SETUP_COMPLETE === status
  ) {
    return null;
  }

  return (
    <NoDataFound
      iconElement={<span style={{ fontSize: 64 }}>👋</span>}
      title={
        <Typography.Title level={3}>{CONTENT[status].title}</Typography.Title>
      }
      subtitle={CONTENT[status].subtitle}
      subtitleStyle={{
        color: COLORS.TEXT1,
      }}
      extra={
        CONTENT[status]?.buttonText && CONTENT[status]?.buttonOnClick ? (
          <div className="card__action">
            <Button
              className="siteBtn darkBtn"
              icon={<ArrowRightOutlined />}
              onClick={CONTENT[status]?.buttonOnClick}
              block>
              {CONTENT[status]?.buttonText}
            </Button>
          </div>
        ) : null
      }
    />
  );
};

export default memo(TQConnectInfoCard);
